import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Check, Images } from "lucide-react";
import { PROJECTS, PROJECT_FILTERS } from "../data/site";
import { Reveal } from "./Reveal";
import { useQuote } from "./QuoteModal";

const stageColor = (s) =>
  s === "Before" ? "bg-black/70 text-white" : s === "After" ? "bg-[#D4AF37] text-black" : "bg-white/15 text-white backdrop-blur";

function ProjectModal({ project, onClose }) {
  const { openQuote } = useQuote();
  const [active, setActive] = useState(0);
  const photo = project.photos[active];

  return (
    <motion.div data-testid="project-modal" className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-[#0D0D0D]/92 backdrop-blur-md overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-md w-full max-w-5xl my-8 overflow-hidden">
        <div className="relative">
          <img src={photo.img} alt={`${project.type} — ${photo.stage}`} className="w-full aspect-[16/9] object-cover" />
          <span className={`absolute top-4 left-4 text-xs uppercase tracking-widest px-3 py-1 rounded-full ${stageColor(photo.stage)}`}>{photo.stage}</span>
          <button data-testid="project-modal-close" onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white hover:text-[#D4AF37] transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="flex gap-2 p-4 pb-0 overflow-x-auto">
          {project.photos.map((p, i) => (
            <button key={i} data-testid={`project-photo-${i}`} onClick={() => setActive(i)}
              className={`relative shrink-0 w-28 aspect-[4/3] rounded-sm overflow-hidden border transition-colors ${i === active ? "border-[#D4AF37]" : "border-white/10 hover:border-white/40"}`}>
              <img src={p.img} alt={p.stage} loading="lazy" className="w-full h-full object-cover" />
              <span className="absolute bottom-1 left-1 text-[9px] uppercase tracking-wider bg-black/70 px-1.5 py-0.5 rounded-sm">{p.stage}</span>
            </button>
          ))}
        </div>

        <div className="p-6 sm:p-8">
          <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">{project.cat}</p>
          <h3 className="font-head text-3xl uppercase tracking-tight">{project.type}</h3>
          <p className="mt-1 text-sm text-[#C8C8C8] flex items-center gap-1.5">
            <MapPin size={13} className="text-[#D4AF37]" strokeWidth={1.5} /> {project.location}
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
            {project.work.map((w) => (
              <div key={w} className="flex items-start gap-2.5 text-sm text-[#E0E0E0]">
                <Check size={15} className="text-[#D4AF37] shrink-0 mt-0.5" strokeWidth={2} /> {w}
              </div>
            ))}
          </div>
          <div className="mt-7 flex flex-wrap items-center gap-4">
            <button data-testid="project-modal-quote" onClick={() => { onClose(); openQuote(); }} className="btn-gold rounded-sm px-6 py-3.5 text-sm">
              Get A Quote For A Similar Project
            </button>
            <span className="text-[11px] text-[#8A8A8A]">Representative imagery — example project</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const items = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.cat === filter);

  return (
    <section id="gallery" className="py-24 sm:py-32 bg-[#1B1B1B]" data-testid="projects-section">
      <div className="container-lux">
        <Reveal className="max-w-3xl mb-10">
          <p className="overline mb-3">Our Recent Work</p>
          <h2 className="font-head text-4xl sm:text-5xl uppercase tracking-tight">
            Roofing projects, <span className="text-gold-metallic">start to finish.</span>
          </h2>
          <p className="mt-4 text-[#C8C8C8]">Before, during and after — see exactly how we transform homes across Stockport, Greater Manchester and Cheshire.</p>
        </Reveal>

        <div className="flex flex-wrap gap-3 mb-10">
          {PROJECT_FILTERS.map((f) => (
            <button key={f} data-testid={`projects-filter-${f.toLowerCase().replace(/\s/g, "-")}`} onClick={() => setFilter(f)}
              className={`font-head uppercase tracking-wider text-xs px-4 py-2 rounded-full border transition-colors ${filter === f ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-[#C8C8C8] hover:border-white/40"}`}>
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <AnimatePresence>
            {items.map((p) => (
              <motion.button key={p.id} layout data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                onClick={() => setOpen(p)}
                className="group relative text-left glass rounded-md overflow-hidden hover:border-[#D4AF37]/60 hover:-translate-y-2 transition-[transform,border-color] duration-400">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img src={p.cover} alt={p.type} loading="lazy" decoding="async"
                    className="w-full h-full object-cover group-hover:scale-[1.08] transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/90 via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-widest bg-black/60 backdrop-blur px-2.5 py-1 rounded-full text-[#D4AF37]">{p.cat}</span>
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-black/60 backdrop-blur px-2.5 py-1 rounded-full text-white flex items-center gap-1.5">
                    <Images size={11} /> {p.photos.length} photos
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-head text-xl uppercase tracking-tight leading-tight">{p.type}</h3>
                  <p className="mt-1.5 text-xs text-[#C8C8C8] flex items-center gap-1.5">
                    <MapPin size={12} className="text-[#D4AF37]" strokeWidth={1.5} /> {p.location}
                  </p>
                  <span className="mt-3 inline-block text-xs uppercase tracking-wider text-[#D4AF37] opacity-70 group-hover:opacity-100 transition-opacity">
                    View Project →
                  </span>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <p className="mt-8 text-xs text-[#8A8A8A] max-w-2xl" data-testid="projects-disclaimer">
          Example projects with representative imagery, shown while our full portfolio of completed Sterling Roofworks jobs is photographed and added.
        </p>
      </div>

      <AnimatePresence>
        {open && <ProjectModal project={open} onClose={() => setOpen(null)} />}
      </AnimatePresence>
    </section>
  );
}
